import { useEffect, useState } from "react";

import {
  AdminApiError,
  createStoragePolicy,
  deleteStoragePolicy,
  listStorageLocations,
  listStoragePolicies,
  updateStoragePolicy,
  type StorageLocations,
  type StoragePolicy,
  type StoragePolicyBody,
} from "../api";

/**
 * Where a project's bytes may live — R2, B2, R2-IA and PiVault, alone or in
 * any combination, per use case. Thin UI over `admin_api.routes.
 * storage_policies`; every rule is enforced server-side, this page only
 * explains it:
 *
 * * the allowed set covers EVERY copy (primary and backup);
 * * a second copy must sit in a different failure domain, on B2 or R2-IA;
 * * a policy that keeps data only on self-hosted hardware, or can never
 *   hold an off-site copy, needs the operator to accept the server's exact
 *   statements (`storage_policy_acknowledgement_required`) — the page shows
 *   them and resends them unchanged, it never writes its own.
 */

const LOCATION_INFO: Record<string, string> = {
  r2: "Cloudflare R2 — hot, free egress",
  b2: "Backblaze B2 — cheapest, cold",
  r2ia: "R2 Infrequent Access — backup copies only",
  pivault: "PiVault — self-hosted at home, tailnet-only",
};
const PRIMARY_CAPABLE = ["r2", "b2", "pivault"];
const ACK_REQUIRED = "storage_policy_acknowledgement_required";

interface Preset {
  label: string;
  body: Omit<StoragePolicyBody, "name" | "acknowledge">;
}

/** Starting points for the common use cases; every field stays editable. */
const PRESETS: Preset[] = [
  {
    label: "Default: R2 hot + cloud backup",
    body: { allowed_locations: ["r2", "b2", "r2ia"], preferred_primary: null, require_backup_copy: false },
  },
  {
    label: "Cheapest: B2 only",
    body: { allowed_locations: ["b2"], preferred_primary: "b2", require_backup_copy: false },
  },
  {
    label: "Fast serving: R2 only",
    body: { allowed_locations: ["r2"], preferred_primary: "r2", require_backup_copy: false },
  },
  {
    label: "Every object twice: R2 + B2",
    body: { allowed_locations: ["r2", "b2"], preferred_primary: "r2", require_backup_copy: true },
  },
  {
    label: "Home + cloud: PiVault primary, B2 backup",
    body: { allowed_locations: ["pivault", "b2"], preferred_primary: "pivault", require_backup_copy: true },
  },
  {
    label: "Home only: PiVault",
    body: { allowed_locations: ["pivault"], preferred_primary: "pivault", require_backup_copy: false },
  },
];

const EMPTY: StoragePolicyBody = {
  name: "",
  allowed_locations: ["r2", "b2", "r2ia"],
  preferred_primary: null,
  require_backup_copy: false,
  acknowledge: [],
};

function errorText(err: unknown): string {
  if (err instanceof AdminApiError) {
    const problems = err.details.problems;
    if (Array.isArray(problems) && problems.length) return problems.join(" ");
  }
  return err instanceof Error ? err.message : String(err);
}

export function StoragePolicies(): JSX.Element {
  const [policies, setPolicies] = useState<StoragePolicy[]>([]);
  const [locations, setLocations] = useState<StorageLocations | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState<StoragePolicyBody>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  // The statements the server asked for, and whether the operator accepted them.
  const [pendingAck, setPendingAck] = useState<string[]>([]);
  const [accepted, setAccepted] = useState(false);

  function reload(): void {
    listStoragePolicies()
      .then(setPolicies)
      .catch((err) => setError(errorText(err)));
    listStorageLocations()
      .then(setLocations)
      .catch((err) => setError(errorText(err)));
  }

  useEffect(reload, []);

  function clearAck(): void {
    setPendingAck([]);
    setAccepted(false);
  }

  function resetForm(): void {
    setForm(EMPTY);
    setEditingId(null);
    clearAck();
  }

  function applyPreset(preset: Preset): void {
    setForm((prev) => ({ ...prev, ...preset.body, acknowledge: [] }));
    clearAck();
  }

  function toggleLocation(loc: string): void {
    setForm((prev) => {
      const allowed = prev.allowed_locations.includes(loc)
        ? prev.allowed_locations.filter((l) => l !== loc)
        : [...prev.allowed_locations, loc];
      const preferred =
        prev.preferred_primary && allowed.includes(prev.preferred_primary) ? prev.preferred_primary : null;
      return { ...prev, allowed_locations: allowed, preferred_primary: preferred, acknowledge: [] };
    });
    clearAck();
  }

  function startEdit(p: StoragePolicy): void {
    setEditingId(p.id);
    setForm({
      name: p.name,
      allowed_locations: p.allowed_locations,
      preferred_primary: p.preferred_primary,
      require_backup_copy: p.require_backup_copy,
      acknowledge: [],
    });
    clearAck();
    setNotice(null);
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!form.name.trim() || form.allowed_locations.length === 0) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    const body: StoragePolicyBody = {
      ...form,
      name: form.name.trim(),
      acknowledge: accepted ? pendingAck : [],
    };
    try {
      const saved = editingId ? await updateStoragePolicy(editingId, body) : await createStoragePolicy(body);
      setNotice(`Saved policy "${saved.name}".`);
      resetForm();
      reload();
    } catch (err) {
      if (err instanceof AdminApiError && err.code === ACK_REQUIRED) {
        const needed = err.details.acknowledgement_required;
        setPendingAck(Array.isArray(needed) ? needed.map(String) : []);
        setAccepted(false);
        setNotice("This policy needs an explicit acknowledgement: read the statements below, accept, and save again.");
      } else {
        setError(errorText(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(p: StoragePolicy): Promise<void> {
    if (!window.confirm(`Delete storage policy "${p.name}"? No object is moved or deleted.`)) return;
    setError(null);
    try {
      await deleteStoragePolicy(p.id);
      if (editingId === p.id) resetForm();
      reload();
    } catch (err) {
      setError(errorText(err));
    }
  }

  const available = new Set(locations?.available_now ?? []);
  const primaryChoices = form.allowed_locations.filter((l) => PRIMARY_CAPABLE.includes(l));

  return (
    <div>
      <h2 className="page-title">🗺️ Storage Policies</h2>
      <p className="page-subtitle">
        Choose where each project&apos;s bytes may live (R2, B2, R2-IA, PiVault), together or on their own. A
        project with no policy uses the platform default (R2 + B2 + R2-IA). Attach a policy on the Projects page.
      </p>
      {error && <div className="error-banner">{error}</div>}
      {notice && <div className="notice-banner">{notice}</div>}

      <div className="panel">
        <h3>{editingId ? "Edit policy" : "New policy"}</h3>
        <div className="form-row">
          <label>Use case</label>
          {PRESETS.map((preset) => (
            <button key={preset.label} type="button" onClick={() => applyPreset(preset)}>
              {preset.label}
            </button>
          ))}
        </div>
        <form onSubmit={(e) => void handleSubmit(e)}>
          <div className="form-row">
            <label htmlFor="policy-name">Name</label>
            <input
              id="policy-name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              maxLength={80}
              required
            />
          </div>
          <div className="form-row">
            <label>Allowed locations</label>
            {(locations?.locations ?? Object.keys(LOCATION_INFO)).map((loc) => (
              <span key={loc} style={{ marginRight: 14 }}>
                <input
                  id={`loc-${loc}`}
                  type="checkbox"
                  checked={form.allowed_locations.includes(loc)}
                  onChange={() => toggleLocation(loc)}
                />{" "}
                <label htmlFor={`loc-${loc}`} style={{ minWidth: 0 }} title={LOCATION_INFO[loc] ?? loc}>
                  {loc}
                  {locations && !available.has(loc) && " (not available now)"}
                </label>
              </span>
            ))}
          </div>
          <div className="form-row">
            <label htmlFor="policy-primary">Preferred primary</label>
            <select
              id="policy-primary"
              value={form.preferred_primary ?? ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, preferred_primary: e.target.value || null, acknowledge: [] }))
              }
            >
              <option value="">automatic (by access pattern)</option>
              {primaryChoices.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label htmlFor="policy-backup">Second copy</label>
            <input
              id="policy-backup"
              type="checkbox"
              checked={form.require_backup_copy}
              onChange={(e) => setForm((prev) => ({ ...prev, require_backup_copy: e.target.checked }))}
            />
            <span className="page-subtitle" style={{ margin: 0 }}>
              keep a second copy of every object, not only critical ones (always in a different failure domain,
              on B2 or R2-IA)
            </span>
          </div>

          {pendingAck.length > 0 && (
            <div className="notice-banner">
              <ul style={{ margin: "0 0 8px 18px" }}>
                {pendingAck.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
              <input
                id="policy-ack"
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
              />{" "}
              <label htmlFor="policy-ack">I understand and accept these statements.</label>
            </div>
          )}

          <button
            type="submit"
            disabled={
              busy ||
              !form.name.trim() ||
              form.allowed_locations.length === 0 ||
              (pendingAck.length > 0 && !accepted)
            }
          >
            {busy ? "Saving…" : editingId ? "Save changes" : "Create policy"}
          </button>{" "}
          {editingId && (
            <button type="button" onClick={resetForm}>
              Cancel
            </button>
          )}
        </form>
        <p className="page-subtitle" style={{ marginTop: 10, fontSize: 12 }}>
          {Object.entries(LOCATION_INFO).map(([loc, info]) => (
            <span key={loc} style={{ display: "block" }}>
              <b>{loc}</b>: {info}
            </span>
          ))}
        </p>
      </div>

      <div className="panel">
        <h3>Policies</h3>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Allowed</th>
              <th>Primary</th>
              <th>Second copy</th>
              <th>Off-site copy possible</th>
              <th>Acknowledged</th>
              <th>Projects</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {policies.map((p) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td>
                  {p.allowed_locations.join(", ")}
                  {p.tailnet_only_locations.length > 0 && (
                    <>
                      <br />
                      <span className="badge" style={{ whiteSpace: "nowrap" }}>tailnet-only: {p.tailnet_only_locations.join(", ")}</span>
                    </>
                  )}
                  {p.unavailable_locations.length > 0 && (
                    <>
                      <br />
                      <span className="badge" style={{ whiteSpace: "nowrap" }}>not available now: {p.unavailable_locations.join(", ")}</span>
                    </>
                  )}
                </td>
                <td>{p.preferred_primary ?? "automatic"}</td>
                <td>{p.require_backup_copy ? "every object" : "critical only"}</td>
                <td>
                  <span className="badge" style={{ whiteSpace: "nowrap" }}>{p.offsite_copy_possible ? "yes" : "no: critical refused"}</span>
                </td>
                <td>
                  {p.acknowledged_by
                    ? `${p.acknowledged_by}${p.acknowledged_at ? ` · ${new Date(p.acknowledged_at).toLocaleString()}` : ""}`
                    : p.acknowledgement_required.length
                      ? "required"
                      : "—"}
                </td>
                <td>{p.projects}</td>
                <td>
                  <button onClick={() => startEdit(p)}>Edit</button>{" "}
                  <button
                    className="danger"
                    disabled={p.projects > 0}
                    title={p.projects > 0 ? "Detach it from every project first" : undefined}
                    onClick={() => void handleDelete(p)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {policies.length === 0 && (
              <tr>
                <td colSpan={8} className="page-subtitle">
                  No policies yet: every project uses the platform default.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
